import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { AlertTriangle, LucideIconData, Check, Circle } from 'lucide-angular';

import { AuthService } from '../../../shared/services/auth.service';
import { AuthModalService } from '../../../shared/services/auth-modal.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  checkStrongPassword,
  strongPasswordValidator,
  StrongPasswordRequirement,
} from '../../../shared/validators/strong-password.validator';
import { matchesField } from '../../../shared/validators/matches-field.validator';
import { IconComponent } from '../../../shared/ui/icon/icon.component';
import { InputComponent } from '../../../shared/ui/input/input.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';

type PageState = 'loading' | 'valid' | 'invalid' | 'submitting';

interface RequirementRow {
  key: StrongPasswordRequirement;
  label: string;
}

const REQUIREMENTS: RequirementRow[] = [
  { key: 'length', label: 'Mínimo 8 caracteres' },
  { key: 'lowercase', label: 'Una minúscula' },
  { key: 'uppercase', label: 'Una mayúscula' },
  { key: 'digit', label: 'Un número' },
  { key: 'special', label: 'Un carácter especial' },
];

@Component({
  selector: 'app-reset-password-page',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    IconComponent,
    InputComponent,
    ButtonComponent,
  ],
  templateUrl: './reset-password-page.component.html',
  styleUrl: './reset-password-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly toastService = inject(ToastService);
  private readonly fb = inject(FormBuilder);

  protected readonly state = signal<PageState>('loading');
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly requirements = REQUIREMENTS;
  protected readonly checkIcon: LucideIconData = Check;
  protected readonly circleIcon: LucideIconData = Circle;
  protected readonly warningIcon: LucideIconData = AlertTriangle;

  private userId = 0;
  private token = '';

  protected readonly form = this.fb.nonNullable.group({
    password: ['', [Validators.required, strongPasswordValidator()]],
    password_confirmation: [
      '',
      [Validators.required, matchesField('password')],
    ],
  });

  private readonly passwordValue = toSignal(
    this.form.controls.password.valueChanges,
    { initialValue: this.form.controls.password.value },
  );

  protected readonly requirementStatus = computed(() =>
    checkStrongPassword(this.passwordValue() ?? ''),
  );

  ngOnInit(): void {
    const params = this.route.snapshot.paramMap;
    const idRaw = params.get('id');
    const token = params.get('token');

    const id = Number(idRaw);
    if (!idRaw || Number.isNaN(id) || !token) {
      this.state.set('invalid');
      return;
    }

    this.userId = id;
    this.token = token;

    this.authService.checkResetToken(token).subscribe({
      next: () => this.state.set('valid'),
      error: () => this.state.set('invalid'),
    });
  }

  protected onSubmit(): void {
    if (this.form.invalid || this.state() === 'submitting') {
      this.form.markAllAsTouched();
      return;
    }

    const { password, password_confirmation } = this.form.getRawValue();
    this.state.set('submitting');
    this.errorMessage.set(null);

    this.authService
      .resetPassword(this.userId, password, password_confirmation, this.token)
      .pipe(finalize(() => {
        if (this.state() === 'submitting') this.state.set('valid');
      }))
      .subscribe({
        next: () => {
          this.toastService.show({
            type: 'success',
            message: 'Contraseña actualizada. Ya puedes iniciar sesión.',
          });
          this.router.navigate(['/']).then(() => {
            this.authModalService.open('login');
          });
        },
        error: () => {
          this.errorMessage.set(
            'Ha ocurrido un error. Por favor, inténtalo de nuevo.',
          );
        },
      });
  }

  protected openForgotModal(): void {
    this.authModalService.open('forgot');
  }
}
